import { DOCUMENT } from '@angular/common';
import { Component, EventEmitter, inject, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';
import { HiDatesService } from '@ddv/common-components';
import { MetadataService, SlowDataApprovalComponent, SlowDataApprovalService } from '@ddv/datasets';
import { legacyUatDomain, uatDomain } from '@ddv/http';
import {
    Client,
    DashboardPreference,
    DateRangeString,
    FilterPreference,
    isTFLIncompleteFiles,
    isTradeFileDetails,
    MODE,
    NamedQuery,
    QueryPeriodTypeName,
    QueryTypeName,
    resolveDateString,
    toDefaultQueryParams,
    UserPreferences,
} from '@ddv/models';
import { DatePickerInfo, DdvWidgetService } from '@ddv/widget-on-boards';
import { DropdownOption } from '@hs/ui-core-common';
import { FuzzyDate, PeriodStatusCollection } from '@hs/ui-core-date';
import { Fund } from '@hs/ui-core-fund';
import { combineLatest, Subscription } from 'rxjs';

import { ActiveDatePickerComponent } from '../active-date-picker/active-date-picker.component';
import { AdditionalFiltersComponent } from '../additional-filters/additional-filters.component';
import { QueryParamsComponent } from '../query-params/query-params.component';

@Component({
    selector: 'ddv-dashboard-filters',
    templateUrl: 'dashboard-filters.component.html',
    styleUrls: ['dashboard-filters.component.scss'],
    imports: [
        QueryParamsComponent,
        ActiveDatePickerComponent,
        AdditionalFiltersComponent,
        SlowDataApprovalComponent,
    ],
})
export class DashboardFiltersComponent implements OnChanges, OnInit, OnDestroy {
    @Input() clientCode = '';
    @Input() id: number | undefined;
    @Input() namedQueries: NamedQuery[] = []; // in MW, this will update when you drill down, in dashboards it will not
    @Input() viewMode: string | undefined | null; // only relevant for opening detail widgets on a dashboard
    @Input() inMultiEnvironment = false;
    @Input() funds: Fund[] = [];
    @Input() clients: Client[] = [];
    @Input() fuzzyDates: FuzzyDate[] = [];
    @Input() userPreferences: UserPreferences | undefined;
    @Input() queryParams: FilterPreference | DashboardPreference = {};
    @Input() bypassSlowDataApproval = false;

    @Output() applyFunds = new EventEmitter<Fund[]>();
    @Output() applyClients = new EventEmitter<Client[]>();
    @Output() applyDateFilter = new EventEmitter<{ startDate?: string, endDate?: string, activeDate?: string | null }>();
    @Output() applyAdditionalFilters = new EventEmitter<Record<string, DropdownOption[]>[] | undefined>();

    private readonly ddvWidgetService = inject(DdvWidgetService);
    private readonly metadataService = inject(MetadataService);
    private readonly slowDataApprovalService = inject(SlowDataApprovalService);
    private readonly document = inject(DOCUMENT);
    private readonly hiDatesService = inject(HiDatesService);

    protected datePickerMode: 'date-range' | 'point-in-time' | 'none' = 'none';
    protected isActiveDateSupported = false;
    protected activeDateMode: 'active-date' | 'date-range' = 'date-range';
    protected clientPickerMode: 'funds' | 'single-client' | 'multi-client' = 'funds';
    protected dateToCheckSlowness: string | DateRangeString | undefined;
    protected showSlowDataApproval = false;
    protected selectedFunds: Fund[] = [];
    protected selectedClients: Client[] = [];
    protected fromDate: string | undefined;
    protected toDate: string | undefined;
    protected periodStatuses: PeriodStatusCollection | undefined;
    protected showAdditionalFilters = false;

    private isQueryTypeRecon = false;
    private latestQueryParams: DashboardPreference | undefined;
    private detailWidgetId: number | undefined;
    private detailWidgetHasActions = false;
    private hasSingleClientNamedQuery = false;
    private detailsWidgetSubscription: Subscription | undefined;
    private defaultSelectedClient = '';
    private currentActiveDate: string | null | undefined = null;
    private periodStatusSubscription: Subscription | undefined;

    ngOnInit(): void {
        this.determineDefaultSelectedClientFromLocation(this.document.location.href);

        // all of this is only relevant for DASHBOARDS
        // as some of the observables in this combineLatest only emit from dashboards
        this.detailsWidgetSubscription = combineLatest([
            this.metadataService.actionsState$,
            this.ddvWidgetService.currentOpenedDetailWidgetId,
            this.ddvWidgetService.datePickerInfo,
        ]).subscribe({
            next: ([actionState, detailWidgetId, datePickerInfo]) => {
                this.detailWidgetId = detailWidgetId;
                this.detailWidgetHasActions = Object.values(actionState).some((actions) => actions.length > 0);

                if (detailWidgetId) {
                    this.onDetailWidgetOpened(datePickerInfo);
                }

                this.determineClientPickerMode();
            },
        });
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes.namedQueries) {
            this.reconfigureForUpdatedQueryTypes();
            this.showAdditionalFilters = this.namedQueries.some((nq) => nq.parameters?.some((p) => p.type === 'additional-filter'));
        }

        this.determineClientPickerMode();

        if (changes.queryParams) {
            this.latestQueryParams = changes.queryParams.previousValue as DashboardPreference ?? {};
            this.processUpdatedQueryParams();
            this.currentActiveDate = this.queryParams?.activeDate;
            this.activeDateMode = this.currentActiveDate ? 'active-date' : 'date-range';
        }
    }

    ngOnDestroy(): void {
        this.detailsWidgetSubscription?.unsubscribe();
    }

    protected updateActiveDateMode(mode: 'active-date' | 'date-range'): void {
        this.activeDateMode = mode;

        if (this.activeDateMode === 'active-date') {
            this.emitActiveDate(this.queryParams.endDate);
        } else {
            this.emitActiveDate(null);
        }
    }

    protected applyActiveDate(activeDate: string): void {
        let dateToEmit = activeDate;
        if (!dateToEmit && (this.queryParams.startDate || this.queryParams.endDate)) {
            dateToEmit = this.queryParams.endDate ?? this.queryParams.startDate ?? '';
        }

        this.emitActiveDate(dateToEmit);
    }

    protected onDateRangeChanged({ to, from }: { to?: string, from?: string }): void {
        this.dateToCheckSlowness = { dateFrom: from, dateTo: to };
        this.confirmSlownessIfNecessary();
    }

    protected onDateChanged(date: string): void {
        this.dateToCheckSlowness = date;
        this.confirmSlownessIfNecessary();
    }

    protected onAdditionalFiltersChanged(additionalFilters: Record<string, DropdownOption[]>[] | undefined): void {
        this.applyAdditionalFilters.emit(additionalFilters);
    }

    protected onSlowDataApprovalResponse(approved: boolean): void {
        this.showSlowDataApproval = false;

        if (approved) {
            this.applyPickerSelectedDates(this.dateToCheckSlowness);
        }
    }

    private determineDefaultSelectedClientFromLocation(href: string): void {
        if (href.includes('localhost') || href.includes(legacyUatDomain) || href.includes(uatDomain)) {
            this.defaultSelectedClient = 'UT58UAT';
        } else {
            this.defaultSelectedClient = 'MLTIPROD';
        }
    }

    private determineClientPickerMode(): void {
        if (this.detailWidgetId) {
            this.clientPickerMode = this.inMultiEnvironment ?
                (this.hasSingleClientNamedQuery || this.detailWidgetHasActions ? 'single-client' : 'multi-client') :
                'funds';
        } else {
            this.clientPickerMode = this.inMultiEnvironment ?
                (this.hasSingleClientNamedQuery ? 'single-client' : 'multi-client') :
                'funds';
        }
    }

    private setSelectedClient(): void {
        if (this.clientPickerMode === 'single-client') {
            const clientId = this.queryParams.clients?.length === 1 ? this.queryParams.clients[0].clientId : this.defaultSelectedClient;
            this.selectedClients = this.clients.filter((client) => client.clientId === clientId);

            // this is really not good.  at this point, we're simply trying to SHOW you the selected client
            // but where we've wandered into a single-client mode (so for all things TFL), the query-params we passed in
            // are actually WRONG unless we emit this.  the responsibility for this should be moved to the parent component(s)
            this.applyClients.emit(this.selectedClients);
        }
    }

    private processUpdatedQueryParams(): void {
        this.selectedFunds = this.funds.filter((ccFund) => {
            return this.queryParams.funds?.some((fund) => fund.fundId === ccFund.code);
        });

        this.selectedClients = this.clients.filter((client) => {
            return this.queryParams.clients?.some((c) => c.clientId === client.clientId);
        });

        this.fromDate = this.queryParams.startDate;
        this.toDate = this.queryParams.endDate;

        this.setSelectedClient();
    }

    private isActiveDateNotInRange(pickerDate: string | DateRangeString | undefined): boolean {
        if (pickerDate === undefined) {
            return true;
        }

        const startDate = resolveDateString(typeof pickerDate === 'string' ? pickerDate : pickerDate.dateFrom, this.fuzzyDates);
        const endDate = resolveDateString(typeof pickerDate === 'string' ? pickerDate : pickerDate.dateTo, this.fuzzyDates);
        const activeDate = resolveDateString(this.queryParams.activeDate ?? undefined, this.fuzzyDates);

        if (activeDate && startDate && endDate) {
            return activeDate.isAfter(endDate) || activeDate.isBefore(startDate);
        }

        return true;
    }

    // this method is only called when the data/date-range are changed, not the active-date
    // so it is unclear why the active date is even involved, but so be it.
    private applyPickerSelectedDates(selectedDate: string | DateRangeString | undefined): void {
        let startDate: string | undefined;
        let endDate: string | undefined;
        let activeDate: string | undefined | null;

        if (selectedDate) {
            if (typeof selectedDate === 'string') {
                startDate = selectedDate;
                endDate = selectedDate;
            } else {
                startDate = selectedDate.dateFrom;
                endDate = selectedDate.dateTo;
            }

            if (this.activeDateMode === 'active-date' && this.isActiveDateSupported) {
                activeDate = this.isActiveDateNotInRange(selectedDate) ? endDate : this.queryParams.activeDate;
            }
        }

        this.applyDateFilter.emit({ startDate, endDate, activeDate });
        this.currentActiveDate = activeDate;
    }

    private onDetailWidgetOpened({ isCalendarVisible, isDateRangeSupported, isActiveDateSupported }: DatePickerInfo): void {
        this.datePickerMode = isCalendarVisible ? (isDateRangeSupported ? 'date-range' : 'point-in-time') : 'none';
        this.isActiveDateSupported = isActiveDateSupported;

        // from here....
        if (this.viewMode === MODE.VIEW) {
            this.setQueryParamsDatesForDetailWidget();
        } else if (this.viewMode === 'edit') { // notice that the MODE enum is not used here.  That's because either its wrong or what is bound here
            this.queryParams.startDate = this.latestQueryParams?.startDate;
            this.queryParams.endDate = this.latestQueryParams?.endDate;
            this.queryParams.activeDate = this.latestQueryParams?.activeDate;
        }
        // ...to here
        // this component writes back to the queryParams object that was sent on the @Input()
        // which is bad

        this.fromDate = this.queryParams.startDate;
        this.toDate = this.queryParams.endDate;
        this.currentActiveDate = this.queryParams?.activeDate;
        this.activeDateMode = this.currentActiveDate ? 'active-date' : 'date-range';
    }

    private confirmSlownessIfNecessary(): void {
        if (this.doNotRequestSlowDataApproval()) {
            return this.applyPickerSelectedDates(this.dateToCheckSlowness);
        }

        this.showSlowDataApproval = true;
    }

    private emitActiveDate(activeDate?: string | null): void {
        this.applyDateFilter.emit({ activeDate });
        this.currentActiveDate = activeDate;
    }

    private reconfigureForUpdatedQueryTypes(): void {
        this.isActiveDateSupported = this.namedQueries.length === 0 ||
            this.namedQueries.some((nq) => nq.periodType === QueryPeriodTypeName.RANGE_WITH_ACTIVE_DATE);

        this.datePickerMode = 'none';
        if (this.namedQueries.length === 0) {
            this.datePickerMode = 'date-range';
        } else {
            if (this.namedQueries.some(isARangeQuery)) {
                this.datePickerMode = 'date-range';
            } else if (this.namedQueries.some((nq) => nq.periodType === QueryPeriodTypeName.POINT_IN_TIME)) {
                this.datePickerMode = 'point-in-time';
            }
        }

        this.hasSingleClientNamedQuery =
            !!this.namedQueries.length &&
            this.namedQueries.every((nq) => isTFLDetailsOrIncompleteFiles(nq.normalizeQueryTypeName()));

        this.isQueryTypeRecon = this.namedQueries.some((nq) => nq.normalizeQueryTypeName() === QueryTypeName.RECON);

        this.loadOrUnloadInvestorPeriods();
    }

    // this is only relevant for hs-dashboard and only in view mode
    // it would be good to pull this up somehow
    private setQueryParamsDatesForDetailWidget(): void {
        const hsDefaults = toDefaultQueryParams([], [], this.namedQueries);

        this.queryParams.startDate ??= this.userPreferences?.startDate ?? hsDefaults.startDate;
        this.queryParams.endDate ??= this.userPreferences?.endDate ?? hsDefaults.endDate;

        // none of this means any sense to me, so i don't really know how to test it
        if (!this.queryParams.activeDate) {
            /* eslint-disable eqeqeq */
            this.activeDateMode = this.queryParams.activeDate !== null ? 'active-date' : 'date-range';

            /* eslint-disable eqeqeq */
            if (this.queryParams.activeDate !== null) {
                this.queryParams.activeDate = this.isActiveDateNotInRange(this.userPreferences?.endDate ?? hsDefaults.endDate ?? '') ?
                    this.queryParams.endDate :
                    this.queryParams.activeDate;
            }
        }
    }

    private doNotRequestSlowDataApproval(): boolean {
        if (this.bypassSlowDataApproval) {
            return true;
        }

        // its really only a thing for recon queries
        if (!this.isQueryTypeRecon) {
            return true;
        }

        // i don't even know how this can happen
        if (!this.dateToCheckSlowness) {
            return true;
        }

        return this.slowDataApprovalService.isApproved(this.clientCode, this.dateToCheckSlowness);
    }

    private loadOrUnloadInvestorPeriods(): void {
        const hasInvestorQuery = this.namedQueries.some((nq) => {
            const queryTypeName = nq.normalizeQueryTypeName();
            return queryTypeName === QueryTypeName.INVESTORS_ACTIVITY ||
                queryTypeName === QueryTypeName.INVESTORS_ASSETS ||
                queryTypeName === QueryTypeName.INVESTORS_PERFORMANCE ||
                queryTypeName === QueryTypeName.ECDI;
        });
        const anyFundIsInvestor = this.funds.some((fund) => fund.availability === 'hi' || fund.availability === 'both');

        if (hasInvestorQuery && anyFundIsInvestor) {
            this.periodStatusSubscription = this.hiDatesService.hiDates()
                .subscribe({
                    next: (hiDates) => {
                        this.periodStatuses = new PeriodStatusCollection(hiDates.map((hd) => hd.toPeriodStatus()));
                    },
                });
        } else {
            this.periodStatuses = undefined;
            this.periodStatusSubscription?.unsubscribe();
        }
    }
}

function isTFLDetailsOrIncompleteFiles(queryTypeName: string): boolean {
    return isTradeFileDetails(queryTypeName) || isTFLIncompleteFiles(queryTypeName);
}

function isARangeQuery(namedQuery: NamedQuery): boolean {
    return namedQuery.periodType === QueryPeriodTypeName.RANGE ||
        namedQuery.periodType === QueryPeriodTypeName.RANGE_WITH_ACTIVE_DATE;
}
